<script setup>
// Placeholder shown while a CRM iframe loads. Same shapes as the CRM's own
// skeletons (crm-frontend/src/shared/ui/Skeleton.jsx) so the hand-off from
// Chatwoot to the iframe looks like one continuous load.
defineProps({
  variant: {
    type: String,
    default: 'page',
    validator: value => ['kanban', 'page'].includes(value),
  },
});

const kanbanColumns = [3, 2, 2, 1, 1];
</script>

<template>
  <div class="crm-skeleton" role="status" aria-busy="true">
    <template v-if="variant === 'kanban'">
      <div class="flex gap-1.5">
        <div class="bone h-9 w-32 rounded-full" />
        <div class="bone h-9 w-28 rounded-full" />
        <div class="bone h-9 w-24 rounded-full" />
      </div>
      <div class="flex items-end justify-between gap-4">
        <div class="flex flex-col gap-2">
          <div class="bone h-7 w-56" />
          <div class="bone h-4 w-72" />
        </div>
        <div class="flex gap-2">
          <div class="bone h-10 w-24" />
          <div class="bone h-10 w-24" />
        </div>
      </div>
      <div class="flex flex-1 min-h-0 gap-4 overflow-hidden">
        <div
          v-for="(cards, column) in kanbanColumns"
          :key="column"
          class="column"
        >
          <div class="bone h-5 w-32" />
          <div class="bone h-3.5 w-24 mb-1" />
          <div v-for="card in cards" :key="card" class="card">
            <div class="flex justify-between gap-3">
              <div class="bone h-4 w-32" />
              <div class="bone h-4 w-14" />
            </div>
            <div class="bone h-5 w-24 rounded-full" />
            <div class="bone h-3.5 w-40" />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-col gap-2 w-full max-w-5xl mx-auto">
        <div class="bone h-7 w-48" />
        <div class="bone h-4 w-80" />
      </div>
      <div
        v-for="block in 2"
        :key="block"
        class="card w-full max-w-5xl mx-auto"
      >
        <div class="flex items-start gap-3">
          <div class="bone h-10 w-10 rounded-xl" />
          <div class="flex flex-col flex-1 gap-2">
            <div class="bone h-5 w-48" />
            <div class="bone h-3.5 w-72" />
          </div>
        </div>
        <div class="bone h-10 w-full" />
        <div class="bone h-10 w-2/3" />
        <div class="bone h-10 w-full" />
      </div>
    </template>
  </div>
</template>

<style scoped>
.crm-skeleton {
  background: rgb(var(--background-color));
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  overflow: hidden;
  padding: 1.5rem;
}

.bone {
  animation: crm-skeleton-pulse 1.6s ease-in-out infinite;
  background: rgb(var(--slate-4));
  border-radius: 0.5rem;
}

.column {
  background: rgb(var(--slate-3));
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-width: 284px;
  padding: 0.75rem;
  width: 300px;
}

.card {
  background: rgb(var(--surface-2));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
}

@keyframes crm-skeleton-pulse {
  50% {
    opacity: 0.55;
  }
}
</style>
